<template>
  <div class="linkedin-profile-tab">
    <div v-if="loading" class="profile-loading">
      <LoadingBars size="sm" />
    </div>

    <div v-else-if="error" class="profile-error">
      <Icon icon="mdi:alert-circle-outline" class="profile-error__icon" />
      <p class="profile-error__message">{{ error }}</p>
      <Button variant="primary" size="sm" @click="$emit('retry')">Try Again</Button>
    </div>

    <div v-else-if="!profile" class="profile-empty">
      <Icon icon="mdi:account-circle-outline" class="profile-empty__icon" />
      <p>No profile data available</p>
    </div>

    <div v-else class="profile-card">
      <div class="profile-header">
        <a
          v-if="profile.profilePictureUrl && !avatarFailed"
          :href="profile.profilePictureUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="profile-avatar profile-avatar--clickable"
        >
          <img
            :src="profile.profilePictureUrl"
            :alt="profile.contactName || 'Profile photo'"
            @error="onAvatarError"
          />
          <div class="profile-avatar__overlay">
            <Icon icon="mdi:open-in-new" class="profile-avatar__icon" />
          </div>
        </a>
        <div v-else class="profile-avatar">
          <div class="profile-avatar__placeholder">
            <Icon icon="mdi:account" width="28" height="28" />
          </div>
        </div>

        <div class="profile-meta">
          <h5>{{ profile.contactName || "" }}</h5>
          <p class="profile-role">{{ profile.contactRole || "" }}</p>
          <p class="profile-location">{{ profile.location || "" }}</p>
        </div>
      </div>

      <div v-if="throttledSections.length" class="profile-throttled">
        Some profile sections may be unavailable right now (rate limited by LinkedIn).
      </div>

      <div v-if="overview.length > 0" class="profile-section">
        <h6>OVERVIEW</h6>
        <ul>
          <li v-for="(item, index) in overview" :key="index">{{ item }}</li>
        </ul>
      </div>

      <div v-if="profile.summary" class="profile-section">
        <h6>ABOUT</h6>
        <div class="profile-summary">{{ profile.summary }}</div>
      </div>

      <div v-if="experience.length > 0" class="profile-section">
        <h6>EXPERIENCE</h6>
        <div class="experience-list">
          <div v-for="(exp, index) in experience" :key="index" class="experience-item">
            <div class="experience-header">
              <div class="experience-title">{{ exp.title }}</div>
              <div class="experience-duration">{{ exp.duration }}</div>
            </div>
            <div class="experience-company">{{ exp.company }}</div>
            <div v-if="exp.location" class="experience-location">{{ exp.location }}</div>
            <div v-if="exp.description" class="experience-description">{{ exp.description }}</div>
          </div>
        </div>
      </div>

      <div v-if="education.length > 0" class="profile-section">
        <h6>EDUCATION</h6>
        <div class="education-list">
          <div v-for="(edu, index) in education" :key="index" class="education-item">
            <div class="education-header">
              <div class="education-school">{{ edu.institution }}</div>
              <div class="education-duration">{{ edu.duration }}</div>
            </div>
            <div v-if="edu.degree" class="education-degree">{{ edu.degree }}</div>
            <div v-if="edu.fieldOfStudy" class="education-field">{{ edu.fieldOfStudy }}</div>
          </div>
        </div>
      </div>

      <div v-if="skills.length > 0" class="profile-section">
        <h6>SKILLS</h6>
        <div class="skills-container">
          <span
            v-for="(skill, index) in displayedSkills"
            :key="index"
            class="skill-tag"
            :title="skill.endorsements ? `${skill.endorsements} endorsements` : ''"
          >
            {{ skill.name }}
            <span v-if="skill.endorsements" class="skill-endorsements">
              ({{ skill.endorsements }})
            </span>
          </span>
        </div>
        <button
          v-if="skills.length > maxDisplayedSkills"
          class="show-more-btn"
          @click="showAllSkills = !showAllSkills"
        >
          {{ showAllSkills ? "Show Less" : `Show ${skills.length - maxDisplayedSkills} More` }}
        </button>
      </div>

      <div v-if="languages.length > 0" class="profile-section">
        <h6>LANGUAGES</h6>
        <div class="languages-list">
          <div v-for="(lang, index) in languages" :key="index" class="language-item">
            <span class="language-name">{{ lang.name }}</span>
            <span v-if="lang.proficiency" class="language-proficiency">{{ lang.proficiency }}</span>
          </div>
        </div>
      </div>

      <div v-if="certifications.length > 0" class="profile-section">
        <h6>CERTIFICATIONS</h6>
        <div class="certifications-list">
          <div v-for="(cert, index) in certifications" :key="index" class="certification-item">
            <div class="certification-name">{{ cert.name }}</div>
            <div v-if="cert.issuingOrganization" class="certification-org">
              {{ cert.issuingOrganization }}
            </div>
            <div v-if="cert.issueDate || cert.expirationDate" class="certification-dates">
              {{ formatCertificationDates(cert.issueDate, cert.expirationDate) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="projects.length > 0" class="profile-section">
        <h6>PROJECTS</h6>
        <div class="projects-list">
          <div v-for="(project, index) in projects" :key="index" class="project-item">
            <div class="project-header">
              <div class="project-name">{{ project.name }}</div>
              <div v-if="project.duration" class="project-duration">{{ project.duration }}</div>
            </div>
            <div v-if="project.description" class="project-description">
              {{ project.description }}
            </div>
            <a
              v-if="project.url"
              :href="project.url"
              target="_blank"
              rel="noopener noreferrer"
              class="project-link"
            >
              <Icon icon="mdi:open-in-new" />
              View Project
            </a>
          </div>
        </div>
      </div>

      <div v-if="volunteering.length > 0" class="profile-section">
        <h6>VOLUNTEERING</h6>
        <div class="volunteering-list">
          <div v-for="(vol, index) in volunteering" :key="index" class="volunteering-item">
            <div class="volunteering-header">
              <div class="volunteering-role">{{ vol.role }}</div>
              <div class="volunteering-duration">{{ vol.duration }}</div>
            </div>
            <div class="volunteering-org">{{ vol.organization }}</div>
            <div v-if="vol.cause" class="volunteering-cause">{{ vol.cause }}</div>
            <div v-if="vol.description" class="volunteering-description">{{ vol.description }}</div>
          </div>
        </div>
      </div>

      <div v-if="recommendations.length > 0" class="profile-section">
        <h6>RECOMMENDATIONS</h6>
        <div class="recommendations-list">
          <div v-for="(rec, index) in recommendations" :key="index" class="recommendation-item">
            <div class="recommendation-content">
              <div class="recommendation-quote-icon">
                <Icon icon="mdi:format-quote-open" />
              </div>
              <div v-if="rec.text" class="recommendation-text">{{ rec.text }}</div>
              <div v-if="rec.caption" class="recommendation-caption">{{ rec.caption }}</div>
            </div>
            <div v-if="rec.actor.fullName" class="recommendation-actor">
              <a
                v-if="rec.actor.profileUrl"
                :href="rec.actor.profileUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="recommendation-actor-link"
              >
                <div
                  v-if="rec.actor.pictureUrl && !recommendationAvatarFailed[index]"
                  class="recommendation-actor-avatar"
                >
                  <img
                    :src="rec.actor.pictureUrl"
                    :alt="rec.actor.fullName"
                    @error="onRecommendationAvatarError(index)"
                  />
                </div>
                <div
                  v-else
                  class="recommendation-actor-avatar recommendation-actor-avatar--placeholder"
                >
                  <Icon icon="mdi:account" width="20" height="20" />
                </div>
                <div class="recommendation-actor-info">
                  <div class="recommendation-actor-name">{{ rec.actor.fullName }}</div>
                  <div v-if="rec.actor.headline" class="recommendation-actor-headline">
                    {{ rec.actor.headline }}
                  </div>
                </div>
                <Icon icon="mdi:open-in-new" class="recommendation-actor-external-icon" />
              </a>
              <div v-else class="recommendation-actor recommendation-actor--no-link">
                <div
                  v-if="rec.actor.pictureUrl && !recommendationAvatarFailed[index]"
                  class="recommendation-actor-avatar"
                >
                  <img
                    :src="rec.actor.pictureUrl"
                    :alt="rec.actor.fullName"
                    @error="onRecommendationAvatarError(index)"
                  />
                </div>
                <div
                  v-else
                  class="recommendation-actor-avatar recommendation-actor-avatar--placeholder"
                >
                  <Icon icon="mdi:account" width="20" height="20" />
                </div>
                <div class="recommendation-actor-info">
                  <div class="recommendation-actor-name">{{ rec.actor.fullName }}</div>
                  <div v-if="rec.actor.headline" class="recommendation-actor-headline">
                    {{ rec.actor.headline }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";

interface MonthYear {
  month?: number;
  year?: number;
}

interface WorkExperience {
  position?: string;
  title?: string;
  company?: string;
  company_name?: string;
  location?: string;
  description?: string;
  start?: string;
  end?: string;
  start_date?: MonthYear;
  end_date?: MonthYear | null;
  current?: boolean;
  is_current?: boolean;
}

interface EducationRaw {
  degree?: string;
  field_of_study?: string;
  school?: string;
  school_name?: string;
  start?: string;
  end?: string;
  start_date?: MonthYear;
  end_date?: MonthYear | null;
}

interface SkillRaw {
  name: string;
  endorsement_count?: number;
}

interface ProfileData {
  contactName?: string;
  contactRole?: string;
  location?: string;
  summary?: string;
  profilePictureUrl?: string;
  workExperience?: WorkExperience[];
  education?: EducationRaw[];
  skills?: (string | SkillRaw)[];
  languages?: Array<{
    name?: string;
    proficiency?: string;
  }>;
  certifications?: Array<{
    name?: string;
    issuing_organization?: string;
    issuingOrganization?: string;
    issue_date?: string;
    issueDate?: string;
    expiration_date?: string | null;
    expirationDate?: string | null;
    credential_id?: string;
    credential_url?: string;
    url?: string;
  }>;
  projects?: Array<{
    name?: string;
    description?: string;
    url?: string;
    credential_url?: string;
    start?: string;
    end?: string | null;
    duration?: string;
  }>;
  volunteeringExperience?: Array<{
    organization?: string;
    role?: string;
    cause?: string;
    description?: string;
    start?: string;
    end?: string | null;
    duration?: string;
  }>;
  recommendations?: {
    received?: Array<{
      text?: string;
      caption?: string;
      actor?: {
        first_name?: string;
        last_name?: string;
        provider_id?: string;
        headline?: string;
        public_identifier?: string;
        public_profile_url?: string;
        profile_picture_url?: string;
      };
    }>;
    received_total_count?: number;
  };
}

interface ExperienceItem {
  title: string;
  company: string;
  duration: string;
  location?: string;
  description?: string;
}

interface EducationItem {
  degree: string;
  institution: string;
  duration: string;
  fieldOfStudy?: string;
}

interface Props {
  profile?: ProfileData | null;
  loading?: boolean;
  error?: string | null;
  throttledSections?: string[];
  mode?: "me" | "contact";
}

const props = withDefaults(defineProps<Props>(), {
  profile: null,
  loading: false,
  error: null,
  throttledSections: () => [],
  mode: "contact",
});

defineEmits<{
  (e: "retry"): void;
}>();

const avatarFailed = ref(false);
const onAvatarError = () => {
  avatarFailed.value = true;
};

const recommendationAvatarFailed = ref<Record<number, boolean>>({});
const onRecommendationAvatarError = (index: number) => {
  recommendationAvatarFailed.value[index] = true;
};

function parseDate(input?: string): Date | null {
  if (!input) return null;

  // Handle "M/D/YYYY" format (e.g., "7/1/2020")
  const dateMatch = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dateMatch) {
    const [, month, day, year] = dateMatch;
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return isNaN(d.getTime()) ? null : d;
  }

  // Try standard date parsing
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function monthYearToDateString(input?: MonthYear | null): string | undefined {
  if (!input?.year) return undefined;
  const month = input.month && input.month >= 1 && input.month <= 12 ? input.month : 1;
  const d = new Date(Date.UTC(input.year, month - 1, 1));
  return d.toISOString();
}

function diffMonths(start: Date, end: Date): number {
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
}

function formatDurationShort(start?: string, end?: string, isCurrent?: boolean): string {
  const s = parseDate(start);
  const e = isCurrent ? new Date() : parseDate(end);
  if (!s || !e) return "";
  const months = Math.max(0, diffMonths(s, e));
  const years = Math.floor(months / 12);
  if (years >= 1) return `${years} yr${years > 1 ? "s" : ""}`;
  if (months >= 1) return `${months} mo${months > 1 ? "s" : ""}`;
  return "";
}

function formatYearRange(start?: string, end?: string): string {
  const s = parseDate(start);
  const e = parseDate(end);
  const sy = s ? s.getFullYear() : null;
  const ey = e ? e.getFullYear() : null;
  if (sy && ey) return `${sy} - ${ey}`;
  if (sy) return `${sy}`;
  if (ey) return `${ey}`;
  return "";
}

function summaryToBullets(summary?: string): string[] {
  if (!summary) return [];
  const trimmed = summary.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 8);
  }
  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (sentences.length <= 1) return [trimmed];
  return sentences.slice(0, 8);
}

const overview = computed(() => summaryToBullets(props.profile?.summary));

interface SkillDisplay {
  name: string;
  endorsements?: number;
}

const showAllSkills = ref(false);
const maxDisplayedSkills = 20;

const skills = computed<SkillDisplay[]>(() => {
  const list = props.profile?.skills || [];
  return list
    .map((s) => {
      if (typeof s === "string") {
        return { name: s.trim() };
      }
      return {
        name: (s?.name || "").trim(),
        endorsements: s?.endorsement_count,
      };
    })
    .filter((s) => s.name);
});

const displayedSkills = computed(() => {
  if (showAllSkills.value) return skills.value;
  return skills.value.slice(0, maxDisplayedSkills);
});

const languages = computed(() => {
  return props.profile?.languages || [];
});

const certifications = computed(() => {
  return props.profile?.certifications || [];
});

const projects = computed(() => {
  const list = (props.profile?.projects || []) as Array<{
    name?: string;
    description?: string;
    url?: string;
    credential_url?: string;
    start?: string;
    end?: string | null;
    duration?: string;
  }>;
  return list.map((p) => ({
    name: p.name || "",
    description: p.description || "",
    url: p.url || p.credential_url || "",
    duration: formatYearRange(p.start, p.end ?? undefined),
  }));
});

const volunteering = computed(() => {
  const list = props.profile?.volunteeringExperience || [];
  return list.map((v) => ({
    role: v.role || "",
    organization: v.organization || "",
    cause: v.cause || "",
    description: v.description || "",
    duration: formatYearRange(v.start, v.end ?? undefined),
  }));
});

const recommendations = computed(() => {
  const received = props.profile?.recommendations?.received || [];
  return received.map((rec) => ({
    text: rec.text || "",
    caption: rec.caption || "",
    actor: {
      firstName: rec.actor?.first_name || "",
      lastName: rec.actor?.last_name || "",
      fullName: [rec.actor?.first_name, rec.actor?.last_name].filter(Boolean).join(" ") || "",
      headline: rec.actor?.headline || "",
      profileUrl: rec.actor?.public_profile_url || "",
      pictureUrl: rec.actor?.profile_picture_url || "",
    },
  }));
});

const experience = computed<ExperienceItem[]>(() => {
  const list = (props.profile?.workExperience || []) as WorkExperience[];
  return list
    .map((e: WorkExperience) => {
      const title = e.title || e.position || "";
      const company = e.company || e.company_name || "";
      const start = e.start || monthYearToDateString(e.start_date);
      const end =
        e.end || (e.end_date === null ? undefined : monthYearToDateString(e.end_date)) || undefined;
      const duration = formatDurationShort(start, end, !!(e.current || e.is_current));
      const location = e.location || "";
      const description = e.description || "";
      return { title, company, duration, location, description };
    })
    .filter((x) => x.title);
});

const education = computed<EducationItem[]>(() => {
  const list = props.profile?.education || [];
  return list
    .map((e) => {
      const degree = [e.degree, e.field_of_study].filter(Boolean).join(", ") || "";
      const institution = e.school || e.school_name || "";
      const start = e.start || monthYearToDateString(e.start_date);
      const end =
        e.end || (e.end_date === null ? undefined : monthYearToDateString(e.end_date)) || undefined;
      const duration = formatYearRange(start, end);
      const fieldOfStudy = e.field_of_study || "";
      return { degree, institution, duration, fieldOfStudy };
    })
    .filter((x) => x.degree || x.institution);
});

const formatCertificationDates = (issueDate?: string, expirationDate?: string | null): string => {
  const issue = issueDate ? parseDate(issueDate) : null;
  const expire = expirationDate ? parseDate(expirationDate) : null;

  if (issue && expire) {
    return `${issue.getFullYear()} - ${expire.getFullYear()}`;
  }
  if (issue) {
    return `Issued ${issue.getFullYear()}`;
  }
  if (expire) {
    return `Expires ${expire.getFullYear()}`;
  }
  return "";
};

const throttledSections = computed(() => props.throttledSections || []);
</script>

<style scoped>
.linkedin-profile-tab {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.profile-card {
  background: #fff;
  border: 1px solid #ececfb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 6px 24px rgba(46, 61, 255, 0.05);
  flex: 1;
  min-height: 0;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.profile-avatar--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.profile-avatar--clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-color: #3e3efe;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-avatar__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;
}

.profile-avatar--clickable:hover .profile-avatar__overlay {
  opacity: 1;
}

.profile-avatar__icon {
  color: #fff;
  font-size: 18px;
}

.profile-avatar__placeholder {
  color: #9ca3af;
}

.profile-meta h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.profile-role {
  margin: 4px 0;
  font-size: 0.85rem;
  color: #5a5a7a;
}

.profile-location {
  font-size: 0.78rem;
  color: #8c8fa7;
  margin-bottom: 0;
}

.profile-section {
  margin-top: 16px;
}

.profile-throttled {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 0.8rem;
  line-height: 1.35;
}

.profile-section:first-of-type {
  margin-top: 16px;
}

.profile-section h6 {
  margin-bottom: 8px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca0c8;
  font-weight: 600;
}

.profile-section ul {
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: #4c4f6a;
  list-style-type: none;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-tag {
  background: #f3f4ff;
  color: #3e3efe;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}

.profile-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #6b7280;
}

.profile-error,
.profile-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.profile-error__icon {
  font-size: 1.5rem;
  color: #ef4444;
  margin-bottom: 0.75rem;
}

.profile-error__message {
  color: #6b7280;
  margin: 0 0 1rem 0;
  max-width: 300px;
  line-height: 1.4;
  font-size: 0.875rem;
}

.profile-empty__icon {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
  color: #9ca3af;
}

.profile-empty p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.profile-summary {
  font-size: 0.875rem;
  color: #4c4f6a;
  line-height: 1.6;
  white-space: pre-wrap;
}

.experience-list,
.education-list,
.certifications-list,
.projects-list,
.volunteering-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.experience-item,
.education-item,
.certification-item,
.project-item,
.volunteering-item {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.experience-header,
.education-header,
.project-header,
.volunteering-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.experience-title,
.education-school,
.project-name,
.volunteering-role {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1a1a2e;
}

.experience-duration,
.education-duration,
.project-duration,
.volunteering-duration {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-left: 12px;
}

.experience-company,
.volunteering-org {
  font-size: 0.8125rem;
  color: #5a5a7a;
  margin-top: 2px;
}

.experience-location,
.volunteering-cause {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 4px;
}

.experience-description,
.project-description,
.volunteering-description {
  font-size: 0.8125rem;
  color: #4c4f6a;
  line-height: 1.5;
  margin-top: 8px;
  white-space: pre-wrap;
}

.education-degree {
  font-size: 0.8125rem;
  color: #5a5a7a;
  margin-top: 2px;
}

.education-field {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
}

.certification-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.certification-org {
  font-size: 0.8125rem;
  color: #5a5a7a;
  margin-bottom: 2px;
}

.certification-dates {
  font-size: 0.75rem;
  color: #9ca3af;
}

.project-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #3e3efe;
  text-decoration: none;
  margin-top: 8px;
  font-weight: 500;
}

.project-link:hover {
  text-decoration: underline;
}

.languages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.language-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
}

.language-name {
  font-size: 0.8125rem;
  color: #1a1a2e;
  font-weight: 500;
}

.language-proficiency {
  font-size: 0.75rem;
  color: #9ca3af;
}

.skill-endorsements {
  font-size: 0.7rem;
  opacity: 0.7;
  margin-left: 4px;
}

.show-more-btn {
  margin-top: 12px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  color: #3e3efe;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.show-more-btn:hover {
  background: #f3f4ff;
  border-color: #3e3efe;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.recommendation-item {
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.recommendation-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.recommendation-content {
  position: relative;
  margin-bottom: 12px;
}

.recommendation-quote-icon {
  position: absolute;
  top: -4px;
  left: -4px;
  color: #3e3efe;
  opacity: 0.3;
  font-size: 24px;
  line-height: 1;
}

.recommendation-text {
  font-size: 0.875rem;
  color: #4c4f6a;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-left: 20px;
  margin-bottom: 8px;
  font-style: italic;
}

.recommendation-caption {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 20px;
  margin-top: 4px;
  font-style: normal;
}

.recommendation-actor {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
}

.recommendation-actor-link {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  width: 100%;
  transition: opacity 0.2s ease;
}

.recommendation-actor-link:hover {
  opacity: 0.8;
}

.recommendation-actor--no-link {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.recommendation-actor-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.recommendation-actor-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.recommendation-actor-avatar--placeholder {
  color: #9ca3af;
}

.recommendation-actor-info {
  flex: 1;
  min-width: 0;
}

.recommendation-actor-name {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #1a1a2e;
  margin-bottom: 2px;
}

.recommendation-actor-link .recommendation-actor-name {
  color: #3e3efe;
}

.recommendation-actor-headline {
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recommendation-actor-external-icon {
  color: #9ca3af;
  font-size: 16px;
  flex-shrink: 0;
  opacity: 0.6;
}
</style>
